import AddIcon from '@mui/icons-material/Add.js';
import CircularProgress from '@mui/material/CircularProgress/index.js';
import Fab from '@mui/material/Fab/index.js';
import Grid from '@mui/material/Grid/index.js';
import Hidden from '@mui/material/Hidden/index.js';
import PropTypes from 'prop-types';
import React, {Fragment, useEffect, useMemo, useRef} from 'react';
import StoryCard from './story-card/index.jsx';
import Tooltip from '@mui/material/Tooltip/index.js';
import Typography from '@mui/material/Typography/index.js';
import {Link as RouterLink} from 'react-router-dom';
import {getStories} from '../../api/stories.js';
import {styled} from '@mui/system';
import {useInfiniteQuery} from '@tanstack/react-query';

const Container = styled('div')(({theme}) => ({
  padding: theme.spacing(4),
  position: 'relative',
}));

const MoreContainer = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(4),
  color: theme.palette.grey[300],
}));

const EmptyState = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto',
});

const CreateButton = styled(Fab)(({theme}) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
}));

const StoryList = ({own = false}) => {
  const moreRef = useRef(null);

  const onCreateClick = () => analytics.track('Create FAB clicked');

  const {data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage} =
    useInfiniteQuery({
      queryKey: ['stories', {own: own}],
      queryFn: ({pageParam}) =>
        getStories({
          own: own,
          before: pageParam,
        }),
      getNextPageParam: lastPage => {
        if (lastPage.more) {
          return lastPage.data[lastPage.data.length - 1].id;
        }

        return undefined;
      },
    });

  const stories = useMemo(
    () => data?.pages.map(page => page.data).flat() || [],
    [data?.pages],
  );

  useEffect(() => {
    let hasFired = false;
    const callback = event => {
      if (moreRef.current) {
        const {innerHeight, scrollY} = event.currentTarget;
        const rect = moreRef.current.getBoundingClientRect();
        if (
          scrollY + innerHeight * 1.25 >= scrollY + rect.top &&
          !isFetchingNextPage &&
          !hasFired
        ) {
          hasFired = true;
          fetchNextPage({pageParam: stories[stories.length - 1].id});
        }
      }
    };
    window.addEventListener('scroll', callback);
    return () => window.removeEventListener('scroll', callback);
  }, [isLoading, own, fetchNextPage, stories, isFetchingNextPage]);

  const renderEmptyState = () => {
    if (isLoading) {
      return <CircularProgress />;
    }
    return (
      <Typography
        variant="h6"
        color="textSecondary"
        data-cy="no-stories-message"
      >
        You have no stories
      </Typography>
    );
  };

  const renderContent = () => {
    if (!stories.length) {
      return <EmptyState>{renderEmptyState()}</EmptyState>;
    }

    return (
      <Container>
        <Grid container spacing={2}>
          {stories.map(story => (
            <Grid item key={story.id} xs={12} sm={6} md={4} lg={3}>
              <StoryCard story={story} />
            </Grid>
          ))}
        </Grid>
        {hasNextPage && (
          <MoreContainer ref={moreRef}>
            <CircularProgress color="inherit" size={32} />
            <Typography color="inherit" variant="subtitle1" sx={{ml: 2}}>
              {isLoading || isFetchingNextPage
                ? 'Loading more stories...'
                : "But wait, there's more!"}
            </Typography>
          </MoreContainer>
        )}
      </Container>
    );
  };

  return (
    <Fragment>
      {renderContent()}
      <Hidden smDown>
        <Tooltip title="Create a story" placement="left">
          <CreateButton
            color="primary"
            component={RouterLink}
            to="/create"
            onClick={onCreateClick}
            data-cy="create-icon"
          >
            <AddIcon />
          </CreateButton>
        </Tooltip>
      </Hidden>
    </Fragment>
  );
};

StoryList.propTypes = {
  own: PropTypes.bool,
};

export default StoryList;
